import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useAppSelector } from 'app/store/store';
import { useGetAnchorageEventsDataMutation } from 'app/api/facade';
import { t } from 'i18next';
import { throttle } from 'app/helpers';
import { Table, Pagination } from 'app/components';
import { Typo } from 'app/components/Typo';
import { getI18n } from 'react-i18next';
import { getDateRange } from '../../helpers';
import { Row } from 'react-table';
import { CountryFlag } from 'CountryFlag/CountryFlag';
import { useFormatMoment } from 'app/hooks/useFormatMoment';
import moment from 'moment/moment';
import { TargetAnchorageEvent } from 'ais-facade-api/api';

import styles from './VesselDetails.module.scss';

type AnchorageTargetEventTypeEnum = 'LOWER_THE_ANCHOR' | 'RAISE_THE_ANCHOR';
export interface AnchoragesTargetEventsRequestI {
  filter: {
    interval: {
      from: string;
      to: string;
    };
    type: AnchorageTargetEventTypeEnum[];
  };
  order: {
    direction: 'ASC' | 'DESC' | 'default';
    by: 'EVENT_DATE_TIME';
  };
}
export const AnchorageTargetEventTypeEnum = {
  LowerTheAnchor: 'LOWER_THE_ANCHOR' as AnchorageTargetEventTypeEnum,
  RaiseTheAnchor: 'RAISE_THE_ANCHOR' as AnchorageTargetEventTypeEnum,
};
export interface SortingInterface {
  direction: 'ASC' | 'DESC' | 'default';
  by: 'EVENT_DATE_TIME';
}
const initialSort: SortingInterface = {
  direction: 'DESC',
  by: 'EVENT_DATE_TIME',
};

const renderCountryCell = (vessel: TargetAnchorageEvent) => {
  if (
    typeof vessel?.anchorage?.port?.meta?.countryName === 'string' &&
    typeof vessel?.anchorage?.port?.countryCode === 'string'
  ) {
    return (
      <>
        <CountryFlag countryCode={vessel.anchorage.port.countryCode.toLocaleLowerCase()} />
        {vessel.anchorage.port.meta.countryName} ({vessel.anchorage.port.countryCode})
      </>
    );
  }

  if (typeof vessel?.anchorage?.iso2 === 'string') {
    return (
      <>
        {vessel.anchorage.iso2 && <CountryFlag countryCode={vessel.anchorage.iso2.toLocaleLowerCase()} />}
        {vessel.anchorage.iso2}
      </>
    );
  }
  return <>&mdash;</>;
};

const renderNameCell = (vessel: TargetAnchorageEvent) => {
  if (typeof vessel?.anchorage?.port?.name === 'string' && typeof vessel?.anchorage?.port?.key === 'string') {
    return (
      <Link to={`/ports/${vessel.anchorage.port.key}`} className={styles.link_in_table}>
        {vessel.anchorage.port.name}
      </Link>
    );
  }

  if (typeof vessel?.anchorage?.port?.name === 'string') {
    return <>{vessel.anchorage.port.name}</>;
  }

  if (typeof vessel?.anchorage?.label === 'string') {
    return <>{vessel.anchorage.label}</>;
  }

  return <>&mdash;</>;
};

const anchoragesEventsTableColumns = [
  {
    Header: 'Name',
    accessor: 'name',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetAnchorageEvent> }) => renderNameCell(vessel),
    canSort: true,
  },
  {
    Header: 'Country',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetAnchorageEvent> }) => renderCountryCell(vessel),
  },
  {
    Header: 'EventType',
    accessor: 'typeEvent',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetAnchorageEvent> }) => (
      <>
        {vessel?.eventType === AnchorageTargetEventTypeEnum.LowerTheAnchor ? (
          t('AtAnchorage')
        ) : vessel?.eventType === AnchorageTargetEventTypeEnum.RaiseTheAnchor ? (
          t('LeftAnchorage')
        ) : (
          <>&mdash;</>
        )}
      </>
    ),
  },
  {
    Header: 'EventDate',
    Cell: ({ row: { original: vessel } }: { row: Row<TargetAnchorageEvent> }) => {
      const formatMoment = useFormatMoment();
      return <>{vessel?.eventDateTime ? formatMoment(moment(vessel.eventDateTime)) : <>&mdash;</>}</>;
    },
    canSort: true,
    sortBy: 'EVENT_DATE_TIME',
  },
];

export const AnchorageEventsTable = ({ mmsi }: { mmsi: number }) => {
  const language = getI18n().language;
  const userConfig = useAppSelector((state) => state.userSlice.uiConfig);
  const { today, fromDate, new_today, new_fromDate } = getDateRange(
    1,
    userConfig && userConfig.callEventMaxDepth ? userConfig?.callEventMaxDepth : 'P720TH',
  );
  const user = useAppSelector((state) => state.userSlice.user);
  const [getAnchorageEvents, { data: anchorageEvents, ...anchorageEventsRequest }] = useGetAnchorageEventsDataMutation();
  const debounceGetTargetsHandler = useCallback(throttle(getAnchorageEvents, 500), []);
  const [sorting, setSorting] = useState<SortingInterface>(initialSort);
  const body: AnchoragesTargetEventsRequestI = useMemo(
    () => ({
      filter: {
        interval: {
          from: fromDate,
          to: today,
        },
        datetime: `${new_fromDate} / ${new_today}`,
        type: [AnchorageTargetEventTypeEnum.LowerTheAnchor, AnchorageTargetEventTypeEnum.RaiseTheAnchor],
      },
      order: sorting.direction === 'default' ? initialSort : sorting,
    }),
    [sorting],
  );

  useEffect(() => {
    if (user && mmsi) {
      setCurrentPage(1);
      debounceGetTargetsHandler({ body, mmsi });
    }
  }, [sorting]);

  useEffect(() => {
    if (user && mmsi) {
      debounceGetTargetsHandler({ body, mmsi });
    }
  }, [user, language]);

  const itemsPerPage = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const tableData = useMemo(
    () => anchorageEvents?.results?.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage),
    [anchorageEvents?.results, currentPage, itemsPerPage],
  );
  const columns = useMemo(() => anchoragesEventsTableColumns, []);

  return (
    <>
      <div className={styles.table_header}>
        <Typo value={`${t('Anchorages')}:`} size="m" />
        <div className={styles.table_actions}></div>
      </div>
      {!anchorageEventsRequest.isLoading && anchorageEvents?.results?.length === 0 && (
        <p className={styles.no_data}>{t('NoResultsFound')}</p>
      )}
      {anchorageEvents?.results && anchorageEvents.results.length > 0 && tableData ? (
        <>
          <div className={styles.table_container}>
            <Table manualSort={true} sorting={sorting} columns={columns} data={tableData} setSorting={setSorting} />
          </div>

          <Pagination
            totalCount={anchorageEvents.results.length}
            currentPage={currentPage}
            pageChangeHandler={setCurrentPage}
            rowsPerPage={20}
          />
        </>
      ) : null}
      {anchorageEventsRequest.isLoading && <span className={styles.loading}>{t('SearchingText')}</span>}
    </>
  );
};
